import { Box, Code, Group, NavLink, rem, ScrollArea, ThemeIcon, UnstyledButton } from '@mantine/core';
import LinksGroup from '@bobex/react-core/components/LinksGroup/NavbarLinksGroups';
import classes from '@bobex/react-core/components/LinksGroup/NavbarLinksGroup.module.css';
import { AccountService } from '@bobex/react-core/services/AccountService';
import { BoNavigationItems } from '@/configs/sidebar.config';
import React, { useEffect } from 'react';
import { CounterApi } from '@bobex/react-core/services/CounterApi';
import { useTranslation } from 'react-i18next';
import { LanguageUtils } from '@bobex/react-core/utils/LanguageUtils';
import { RecentModelAPI } from '@bobex/react-core/domain/RecentModel/RecentModelAPI';
import { RecentAbstractView } from '@bobex/react-core/domain/RecentModel/view/RecentAbstractView';
import { Link, useLocation } from 'react-router-dom';
import { headerLinks, isHeaderLinkActive } from '@/components/Header/Header';

import { useSidebarUpdate } from './SidebarProvider';
import { AssignationSalesModelAPI } from '@bobex/react-core/domain/AssignationSalesModel/AssignationSalesModelAPI';
import sidebarClasses from './Sidebar.module.css';

export default function Sidebar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // const userAuthority = AccountService.getAuthorities();
  // const colorScheme = useMantineColorScheme();

  const [counterMap, setCounterMap] = React.useState<{}>({});
  const [history, setHistory] = React.useState<RecentAbstractView[]>([]);
  const [languageId, setLanguageId] = React.useState<number>(
    LanguageUtils.getCurrentLanguageId() ?? 2
  );
  const sidebarUpdateContext = useSidebarUpdate();

  useEffect(() => {
    // Initial fetch
    getCounters().then();
    getHistory().then();

    // Set up interval to refresh every minute
    const intervalId = setInterval(() => {
      getCounters().then();
    }, 60000); // 60 000 milliseconds = 1 minute

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const onLanguageChanged = (lng: string) => {
      setLanguageId(LanguageUtils.getCurrentLanguageId() ?? 2);
    };

    i18n.on('languageChanged', onLanguageChanged);

    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [i18n]);

  useEffect(() => {
    getHistory().then();
  }, [sidebarUpdateContext.triggerHistoryRefresh]);

  const getCounters = async () => {
    try {
      const response = await CounterApi.getSidebarCounter();
      response['assigned-sales.total'] = await AssignationSalesModelAPI.getMyProspectsCounter();
      setCounterMap(response);
    } catch (error) {
      console.error(error);
    }
  };

  const getHistory = async () => {
    RecentModelAPI.getHistory()
      .then((response) => {
        setHistory(response);
      })
      .catch((error) => {
        console.error('Error fetching recent history', error);
      });
  };

  const links = BoNavigationItems(languageId, t, history).map((group, index) => {
    if (group.label !== 'Logout') {
      if (AccountService.hasAuthority(group.authority)) {
        return <LinksGroup counters={counterMap} group={group} key={index} />;
      }
    } else {
      return (
        <UnstyledButton
          onClick={() => {
            AccountService.logout().then();
          }}
          className={classes.control}
          key={group.label}
        >
          <Group justify="space-between" gap={0}>
            <Box style={{ display: 'flex', alignItems: 'center' }}>
              <ThemeIcon variant="light" size={30}>
                {group.icon && <group.icon style={{ width: rem(18), height: rem(18) }} />}
              </ThemeIcon>
              <Box ml="md">{t(group.label)}</Box>
            </Box>
          </Group>
        </UnstyledButton>
      );
    }
  });

  return (
    <Box style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <ScrollArea style={{ flex: 1, minHeight: 0 }} scrollbars="y">
        <nav className={sidebarClasses.sidebarNav}>
          <Box hiddenFrom="lg">
            {headerLinks.map((hl) => (
              <NavLink
                key={hl.label}
                component={Link}
                to={hl.link}
                label={hl.label}
                active={isHeaderLinkActive(location.pathname, hl.link)}
                leftSection={
                  <ThemeIcon color={hl.color} variant="filled" size={30} radius="md">
                    <hl.icon size={18} />
                  </ThemeIcon>
                }
                style={{paddingLeft: 0}}
              />
            ))}
          </Box>
          <div>{links}</div>

          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '0',
              gap: rem(6),
            }}
          >
            <Code style={{ fontSize: rem(10), marginTop: rem(12) }}>v1.0.0</Code>
            <Code style={{ fontSize: rem(10), marginTop: rem(12) }}>{AccountService.getUser()?.hostname || window.location.hostname}</Code>
          </Box>
        </nav>
      </ScrollArea>
    </Box>
  );
}
